import type { ComponentChildren, VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ActionRow, Button, Chip, CountRow, EmptyState, List, Row, toneFor } from '@ecmb/ui';
import {
  cancelBookingRun,
  INBOX_POLL_MS,
  listTodoPiles,
  markRunTravelled,
  sendQuoteRequest,
  type ApiFilteredBookingRow,
  type ApiTodoPile,
  type ApiTodoPiles,
} from '../../lib/actions.js';
import { ApiError } from '../../lib/api.js';
import { channelLabel, isChannelConfigured, type Registry } from '../../store/registry.js';
import { civilDateLabel, relativeAge } from '../../lib/format.js';
import { ACTION_CHIP_WORDS, chipWords, noteOf } from '../../../shared/booking-chip-words.js';
import { BOOKINGS_WORDS, bookingRowLabelParts, rowMoneySlot, whereLine, whoCell, withoutHome } from '../Bookings.js';
import { Banner } from '../../components/Banner.js';
import { ACTION_WORDS, actionsFor, resendChannel, type TodoAction, type TodoPileName } from './todo-piles.js';

/**
 * ⭐⭐ Board 2812 M3 (plan step 3, R-5, R-2812-f) - A CHANNEL'S TO DO: a count row
 * (Need you now, Waiting on the customer for web only, Coming up, Need an outcome),
 * each count jumping to its pile, then the piles.
 *
 * ⛔ THE PILE RULES LIVE ON THE SERVER. Waiting on the customer, Coming up and Need
 * an outcome are M1's `/api/todo-piles` rows, with their true `total`s; Need you
 * now is the host's (web: the inbox enquiries plus the Unsent pile, Q246; homes:
 * 2814's `needsHelenNow` list). The actions each card offers come from the row's
 * own server flags, through the action table (`todo-piles.ts`) only.
 * ⛔ THE SOONEST CARD IS OPEN, ONE AT A TIME, AND THE OPEN CARD HOLDS THE SCREEN'S
 * ONLY GOLD (R-2812-f): one `openKey` for the whole screen. Need you now comes
 * first, so its first card is the default; with Need you now empty, the first card
 * of the next pile that has one. On homes, 2814's list opens its own soonest row,
 * so a pile card opening tells that list to close (`suppressOpen`), and a row it
 * opens closes the pile card (`onOpenRow`).
 * ⛔ EVERY DESTRUCTIVE CONFIRMS IN PLACE, IS NEVER GOLD AND SITS IN THE END SLOT
 * (DESIGN §7.4, R-2812-d).
 */

/** One Need you now card the host supplies (web: an enquiry), placed by its trip day. */
export type NeedCard = {
  key: string;
  /** The trip's civil day and time, for the soonest-first merge (undated last). */
  day: string | null;
  time: string | null;
  render: (open: boolean, onToggle: () => void) => VNode;
};

export type TodoPilesProps = {
  channel: 'web' | 'homes';
  registry: Registry;
  onOpenBooking: (bookingId: number) => void;
  /** Called after every write here, so the host moves its badge with no poll tick. */
  onChanged: () => void;
  /** web: the inbox's enquiry cards; `null` while they cannot be read. */
  enquiries?: NeedCard[] | null;
  /** web: the inbox read failed (its own banner says so). */
  enquiriesFailed?: boolean;
  /** homes: Need you now is 2814's list, rendered by the host with the open rule.
   *  ⭐ board 2812 M3 fix round 2: `onChanged` is what the list calls after each of its
   *  writes (the To do reloads its piles, WT-2, then the host moves its badge), and
   *  `arrange` places the list's Need you now and its Done list in this To do's order
   *  (Done LAST, UX-8 / PA-6). */
  homesNeed?: {
    count: number | null;
    render: (ctl: {
      suppressOpen: boolean;
      onOpenRow: () => void;
      onChanged: () => void;
      arrange: (needs: ComponentChildren, done: ComponentChildren) => ComponentChildren;
    }) => ComponentChildren;
  };
  /** ⭐ board 2812 M4 (plan step 4): homes, one home's piles (its group key, Q240); its
   *  cards drop the home's name, which the page names once. */
  home?: string;
  /** ⭐ board 2812 M4: the homes column sits beside the piles (the Row's layout). */
  beside?: boolean;
  /** ⭐ board 2812 M4 (step 2): the home's key is unknown to the server (404), so the
   *  host falls back to All homes. */
  onStale?: () => void;
};

/** The pile names and their words (R-11: every new word is listed for Vicky). */
export const PILE_WORDS = {
  need: { title: 'Need you now', caption: 'soonest trip first' },
  wait: { title: 'Waiting on the customer', caption: 'quote sent 3 to 14 days ago, oldest first' },
  soon: { title: 'Coming up', caption: 'confirmed, next 7 days' },
  outcome: { title: 'Need an outcome', caption: 'the date has passed' },
} as const;

/** The empty line each pile shows (neutral, §7.2). */
const PILE_EMPTY: Readonly<Record<'wait' | 'soon' | 'outcome', string>> = {
  wait: 'No quotes are waiting on an answer.',
  soon: 'Nothing confirmed in the next 7 days.',
  outcome: 'No past runs need an outcome.',
};

export const TODO_WORDS = {
  unsent: 'Quote saved, not sent',
  /** ⭐ Alex's ruling Q273(c): every send of the saved quote failed, so it is still unsent
   *  and the row says so plainly, on its note line (gold, "needs a person"), never a chip. */
  sendFailed: {
    sms: 'The quote text did not go through. Send it again.',
    email: 'The quote email did not go through. Send it again.',
  },
  showAll: (n: number) => `Show all ${String(n)}`,
  showingSome: (shown: number, n: number) => `Showing ${String(shown)} of ${String(n)}`,
  pilesUnread: 'The To do lists could not be read just now. The bookings are safe on the server.',
  ngaQuestion: 'Not going ahead? The job ends and nothing is sent to the customer.',
  ngaYes: 'Yes, not going ahead',
  ngaKeep: 'Keep the job',
  sentAgain: 'The quote went out again.',
  markedTravelled: 'Marked as travelled.',
  endedJob: 'The job is not going ahead.',
  done: 'Done',
} as const;

/** The pile `limit` for Show all (M1: up to 500, with the true total). */
const SHOW_ALL_LIMIT = 500;

type PilesState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; piles: ApiTodoPiles };

/** The card-local confirm and notice, by card key. ⭐ board 2812 M3 fix round 2 (WT-4): a
 *  card's notice also names its BOOKING, because card keys are positions and the card may
 *  have left its pile on the reload (a run marked travelled leaves Need an outcome); then
 *  the notice is said at the top of the To do, never on whichever card took its place. */
type Notice = { key: string; bookingId?: number; kind: 'ok' | 'error'; text: string };

function compareDayTime(
  a: { day: string | null; time: string | null },
  b: { day: string | null; time: string | null },
): number {
  if (a.day !== b.day) {
    if (a.day === null) return 1;
    if (b.day === null) return -1;
    return a.day < b.day ? -1 : 1;
  }
  if (a.time === b.time) return 0;
  if (a.time === null) return 1;
  if (b.time === null) return -1;
  return a.time < b.time ? -1 : 1;
}

export function TodoPiles({
  channel,
  registry,
  onOpenBooking,
  onChanged,
  enquiries,
  enquiriesFailed,
  homesNeed,
  home,
  beside,
  onStale,
}: TodoPilesProps) {
  const [state, setState] = useState<PilesState>({ kind: 'loading' });
  /** `undefined` = nobody has chosen, so the default (soonest) card is open;
   *  `null` = she closed it. */
  const [openKey, setOpenKey] = useState<string | null | undefined>(undefined);
  /** homes: false once a pile card has been opened, until she opens a request. */
  const [requestsMayOpen, setRequestsMayOpen] = useState(true);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  /** Piles read in full by Show all, keyed by pile. */
  const [full, setFull] = useState<Partial<Record<TodoPileName, ApiTodoPile>>>({});
  const latest = useRef(0);
  const inFlight = useRef(false);

  const load = (quiet: boolean) => {
    const seq = ++latest.current;
    return listTodoPiles({ channel, ...(home === undefined ? {} : { home }) })
      .then((piles) => {
        if (seq === latest.current) setState({ kind: 'ready', piles });
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404 && onStale !== undefined) {
          onStale();
          return;
        }
        if (!quiet && seq === latest.current) setState({ kind: 'error' });
        throw e;
      });
  };

  useEffect(() => {
    load(false).catch(() => undefined);
    const id = setInterval(() => {
      if (inFlight.current) return;
      load(true).catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 429)) clearInterval(id);
      });
    }, INBOX_POLL_MS);
    return () => clearInterval(id);
  }, []);

  const piles = state.kind === 'ready' ? state.piles : null;
  const pileOf = (name: TodoPileName): ApiTodoPile | undefined => full[name] ?? piles?.[name];

  // ---- the cards, in screen order --------------------------------------------
  const unsentRows = channel === 'web' ? (pileOf('unsent')?.rows ?? []) : [];
  const needCards: Array<{ key: string; day: string | null; time: string | null; node: (open: boolean, toggle: () => void) => VNode }> =
    channel === 'web'
      ? [
        ...(enquiries ?? []).map((c) => ({ key: c.key, day: c.day, time: c.time, node: c.render })),
        ...unsentRows.map((r, i) => ({
          key: `todo-unsent-${String(i)}`,
          day: r.journey_date,
          time: r.journey_time,
          node: (open: boolean, toggle: () => void) => pileCard('unsent', r, `todo-unsent-${String(i)}`, open, toggle),
        })),
      ].sort(compareDayTime)
      : [];
  const pileKeys = (name: 'wait' | 'soon' | 'outcome'): string[] =>
    (pileOf(name)?.rows ?? []).map((_, i) => `todo-${name}-${String(i)}`);
  const otherPiles: Array<'wait' | 'soon' | 'outcome'> = channel === 'web' ? ['wait', 'soon', 'outcome'] : ['soon', 'outcome'];

  /** The default open card: Need you now's first, else the first of the next pile. */
  const homesNeedOpens = channel === 'homes' && (homesNeed?.count ?? 0) > 0;
  const defaultKey: string | null = channel === 'web' && needCards.length > 0
    ? needCards[0]!.key
    : homesNeedOpens
      ? null
      : otherPiles.map((p) => pileKeys(p)[0]).find((k) => k !== undefined) ?? null;
  const isOpen = (key: string): boolean => (openKey === undefined ? key === defaultKey : openKey === key);
  const toggle = (key: string) => {
    const opening = !isOpen(key);
    setOpenKey(opening ? key : null);
    setConfirming(null);
    if (opening) setRequestsMayOpen(false);
  };

  // ---- the writes ------------------------------------------------------------
  const run = async (key: string, bookingId: number, act: () => Promise<{ ok: boolean; text: string }>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(key);
    setNotice(null);
    try {
      const { ok, text } = await act();
      setNotice({ key, bookingId, kind: ok ? 'ok' : 'error', text });
      setConfirming(null);
      setFull({});
      await load(true).catch(() => undefined);
      onChanged();
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const doAction = (action: TodoAction, r: ApiFilteredBookingRow, key: string) => {
    switch (action) {
      case 'open-quote':
      case 'open-booking':
        onOpenBooking(r.booking_id);
        return;
      case 'send-again': {
        const channelTo = resendChannel(r);
        if (channelTo === null) return;
        void run(key, r.booking_id, async () => {
          // The server reuses the live deposit link (`app.ts`), so a web job resends
          // WITH its deposit; an account run has no deposit stage.
          const out = await sendQuoteRequest(r.booking_id, r.money_kind === 'web', channelTo);
          return out.ok ? { ok: true, text: TODO_WORDS.sentAgain } : { ok: false, text: out.message };
        });
        return;
      }
      case 'mark-travelled':
        void run(key, r.booking_id, async () => {
          const out = await markRunTravelled(r.booking_id);
          return out === 'marked' || out === 'already'
            ? { ok: true, text: TODO_WORDS.markedTravelled }
            : { ok: false, text: 'Marking this run as travelled did not finish. The list has been refreshed.' };
        });
        return;
      case 'not-going-ahead':
        setConfirming(key);
        return;
    }
  };

  const endJob = (r: ApiFilteredBookingRow, key: string) => {
    void run(key, r.booking_id, async () => {
      const out = await cancelBookingRun(r.booking_id, undefined, 'not-going-ahead');
      return out.kind === 'cancelled' || out.kind === 'already-cancelled'
        ? { ok: true, text: TODO_WORDS.endedJob }
        : { ok: false, text: 'Not going ahead was refused, so nothing changed. The list has been refreshed.' };
    });
  };

  // ---- one pile card -----------------------------------------------------------
  function pileCard(pile: TodoPileName, r: ApiFilteredBookingRow, key: string, open: boolean, onToggle: () => void): VNode {
    // ⭐ board 2812 M4: on a home's page the card drops the home's name.
    const parts = home === undefined ? bookingRowLabelParts(r) : withoutHome(bookingRowLabelParts(r));
    const chip = chipWords(r);
    // ⭐ board 2812 M4: a balance flag rides the line gold-700 (`whereLine`), as on Bookings.
    // ⭐ Q273(c): an unsent quote whose sends all failed says so instead, flagged gold.
    const failedOn = pile === 'unsent' ? r.quote_send_failed : null;
    const note = failedOn === 'sms' || failedOn === 'email'
      ? { words: TODO_WORDS.sendFailed[failedOn], flag: true }
      : pile === 'unsent' ? TODO_WORDS.unsent : noteOf(r);
    const route = [r.pickup, r.destination].filter(Boolean).join(' to ');
    const money = rowMoneySlot(r);
    const plan = actionsFor(pile, r, (c) => isChannelConfigured(registry, c));
    const channelTo = resendChannel(r);
    const hintId = `${key}-hint`;
    const noticeHere = notice !== null && notice.bookingId === r.booking_id ? notice : null;
    return (
      <Row
        key={key}
        id={key}
        opens="in-place"
        expanded={open}
        onOpen={onToggle}
        /* ⭐ board 2812 M3 fix round 2 (UX-7): never an empty when cell. */
        when={r.journey_date !== null ? civilDateLabel(r.journey_date, r.journey_time) : BOOKINGS_WORDS.noDate}
        /* ⛔ THE REFERENCE NEVER BREAKS MID-WORD (board 2812 M3 fix round, Captain's item 4):
           its own `.job-ref-value` span, as the Bookings row has it. */
        who={whoCell(parts)}
        where={whereLine([route || null, r.accessible_vehicle ? 'Accessible vehicle' : null, note])}
        money={money === '' ? undefined : money}
        /* ⭐⭐ board 2812 M3 fix round (Captain's item 1): a saved, unsent quote is in Need you
           now, so its chip says the ACTION ("Send the quote"), in the neutral box, never a
           tone; every other pile keeps the folded status words (R-2812-h). */
        /* ⭐ board 2812 M3 fix round 3 (F3-6): a Need an outcome row needs Helen too, so its
           chip names the step ("Mark the outcome"), neutral, never a green "Confirmed". */
        chip={pile === 'unsent'
          ? <Chip neutral>{ACTION_CHIP_WORDS.sendQuote}</Chip>
          : pile === 'outcome'
            ? <Chip neutral>{ACTION_CHIP_WORDS.markOutcome}</Chip>
            : <Chip neutral tone={chip.toneStatus === null ? undefined : toneFor('booking', chip.toneStatus)}>{chip.words}</Chip>}
      >
        {pile === 'wait' && r.quote_sent_at !== null && (
          <p class="field-hint">Quote sent {relativeAge(r.quote_sent_at)}</p>
        )}
        {noticeHere !== null && <Banner kind={noticeHere.kind}>{noticeHere.text}</Banner>}
        {confirming === key ? (
          <>
            <p class="field-hint" id={hintId}>{TODO_WORDS.ngaQuestion}</p>
            <ActionRow
              end={(
                <Button variant="destructive-quiet" ariaDescribedBy={hintId} disabled={busy !== null} onClick={() => endJob(r, key)}>
                  {TODO_WORDS.ngaYes}
                </Button>
              )}
            >
              <Button variant="quiet" onClick={() => setConfirming(null)}>{TODO_WORDS.ngaKeep}</Button>
            </ActionRow>
          </>
        ) : (
          <>
            {plan.primary === 'send-again' && channelTo !== null && (
              <p class="field-hint" id={hintId}>
                Goes to the customer by {channelLabel(registry, channelTo)}, with the same deposit link.
              </p>
            )}
            <ActionRow
              end={plan.end === null ? undefined : (
                <Button variant="destructive-quiet" disabled={busy !== null} onClick={() => doAction(plan.end!, r, key)}>
                  {ACTION_WORDS[plan.end]}
                </Button>
              )}
            >
              {plan.primary !== null && (
                <Button
                  variant="primary"
                  disabled={busy !== null}
                  ariaDescribedBy={plan.primary === 'send-again' ? hintId : undefined}
                  onClick={() => doAction(plan.primary!, r, key)}
                >
                  {ACTION_WORDS[plan.primary]}
                </Button>
              )}
              {plan.quiet.map((a) => (
                <Button key={a} variant="quiet" disabled={busy !== null} onClick={() => doAction(a, r, key)}>
                  {ACTION_WORDS[a]}
                </Button>
              ))}
            </ActionRow>
          </>
        )}
      </Row>
    );
  }

  // ---- the count row and the piles --------------------------------------------
  const headingId = (pile: string) => `todo-${channel}-${pile}`;
  const jump = (pile: string) => {
    const h = document.getElementById(headingId(pile));
    if (h === null) return;
    h.focus();
    if (typeof h.scrollIntoView === 'function') h.scrollIntoView({ block: 'start' });
  };
  const unsentTotal = channel === 'web' ? piles?.unsent?.total : undefined;
  const needCount: number | null = channel === 'homes'
    ? (homesNeed?.count ?? null)
    : enquiries === null || enquiries === undefined || unsentTotal === undefined
      ? null
      : enquiries.length + unsentTotal;
  const totalOf = (p: 'wait' | 'soon' | 'outcome'): number | null => piles?.[p]?.total ?? null;

  const showAll = (pile: 'wait' | 'soon' | 'outcome') => {
    void listTodoPiles({ channel, ...(home === undefined ? {} : { home }), pile, limit: SHOW_ALL_LIMIT })
      .then((one) => {
        const p = one[pile];
        if (p !== undefined) setFull((prev) => ({ ...prev, [pile]: p }));
      })
      .catch(() => setNotice({ key: `pile-${pile}`, kind: 'error', text: 'The whole list could not be read just now. Try again in a moment.' }));
  };

  const pileSection = (pile: 'wait' | 'soon' | 'outcome') => {
    const p = pileOf(pile);
    const rows = p?.rows ?? [];
    const total = p?.total ?? 0;
    const pileNotice = notice !== null && notice.key === `pile-${pile}` ? notice : null;
    return (
      <section key={pile} class="day-group">
        <h2 class="day-heading" id={headingId(pile)} tabIndex={-1}>
          {p === undefined ? '' : `${String(total)} `}{PILE_WORDS[pile].title}{' '}
          <span class="field-hint">{PILE_WORDS[pile].caption}</span>
        </h2>
        {pileNotice !== null && <Banner kind={pileNotice.kind}>{pileNotice.text}</Banner>}
        {/* ⭐ board 2812 M4 fix round (L1): BESIDE THE HOMES COLUMN an empty pile is its
            heading alone ("0 Coming up, confirmed, next 7 days" already says it is empty).
            Its empty-state card was 74px a pile, and measured at 1024x700 on the seat it
            pushed Glencree's open Need an outcome card's gold "Mark as travelled" to 705-761,
            below the ~700 plane (R11). The soonest card stays open (R-2812-f); the full
            width To do (web enquiries) keeps its sentence. */}
        {p !== undefined && rows.length === 0 && beside !== true && <EmptyState headline={PILE_EMPTY[pile]} />}
        {rows.length > 0 && (
          <List label={PILE_WORDS[pile].title} beside={beside}>
            {rows.map((r, i) => {
              const key = `todo-${pile}-${String(i)}`;
              return pileCard(pile, r, key, isOpen(key), () => toggle(key));
            })}
          </List>
        )}
        {rows.length < total && full[pile] === undefined && (
          <Button variant="quiet" onClick={() => showAll(pile)}>{TODO_WORDS.showAll(total)}</Button>
        )}
        {full[pile] !== undefined && rows.length < total && (
          <p class="helper-note">{TODO_WORDS.showingSome(rows.length, total)}</p>
        )}
      </section>
    );
  };

  /* ⭐ board 2812 M3 fix round 2 (WT-4): an outcome whose card has LEFT the screen on the
     reload (marked travelled, not going ahead) is said here, in a status line, so the
     announcement is not lost with the card. */
  const shownBookings = new Set(
    [...unsentRows, ...otherPiles.flatMap((p) => pileOf(p)?.rows ?? [])].map((r) => r.booking_id),
  );
  const orphan = notice !== null && notice.bookingId !== undefined && !shownBookings.has(notice.bookingId) ? notice : null;

  const layout = (homesNeedList: ComponentChildren, doneList: ComponentChildren) => (
    <>
      <CountRow
        label="To do"
        items={[
          { key: 'need', label: PILE_WORDS.need.title, count: needCount, onSelect: () => jump('need') },
          ...otherPiles.map((p) => ({ key: p, label: PILE_WORDS[p].title, count: totalOf(p), onSelect: () => jump(p) })),
        ]}
      />
      {orphan !== null && <Banner kind={orphan.kind}>{orphan.text}</Banner>}
      {state.kind === 'error' && (
        <Banner kind="error">
          {TODO_WORDS.pilesUnread}{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={() => { load(false).catch(() => undefined); }}>
            Try again
          </button>
        </Banner>
      )}
      <section class="day-group">
        <h2 class="day-heading" id={headingId('need')} tabIndex={-1}>
          {needCount === null ? '' : `${String(needCount)} `}{PILE_WORDS.need.title}{' '}
          <span class="field-hint">{PILE_WORDS.need.caption}</span>
        </h2>
        {homesNeedList}
        {channel === 'web' && needCards.length === 0 && enquiries !== null && enquiries !== undefined && !enquiriesFailed && (
          <EmptyState headline="No new enquiries." evidence="Website bookings land here the moment they arrive." />
        )}
        {channel === 'web' && needCards.length > 0 && (
          <List label={PILE_WORDS.need.title} beside={beside}>
            {needCards.map((c) => c.node(isOpen(c.key), () => toggle(c.key)))}
          </List>
        )}
      </section>
      {otherPiles.map(pileSection)}
      {/* ⭐⭐ board 2812 M3 fix round 2 (UX-8 / PA-6): the quiet Done list sits LAST,
          after Need an outcome, headed "Done" with no count. */}
      {doneList !== null && doneList !== undefined && (
        <section class="day-group">
          <h2 class="day-heading">{TODO_WORDS.done}</h2>
          {doneList}
        </section>
      )}
    </>
  );

  if (channel === 'homes' && homesNeed !== undefined) {
    return (
      <>
        {homesNeed.render({
          suppressOpen: !requestsMayOpen,
          onOpenRow: () => {
            setRequestsMayOpen(true);
            setOpenKey(null);
            setConfirming(null);
          },
          // ⭐ WT-2: a write on a request card reloads THIS screen's piles (a confirm
          // can land a run in Coming up), then the host moves its badge.
          onChanged: () => {
            setFull({});
            load(true).catch(() => undefined);
            onChanged();
          },
          arrange: layout,
        })}
      </>
    );
  }
  return layout(null, null);
}
